/**
 * How tall the app draws itself, and how much of the screen a keyboard has taken.
 *
 * iOS does not shrink the page when the keyboard opens: the layout stays the height of the screen and the browser
 * scrolls it, which leaves the message box sitting above a gap of our own background. The visual viewport knows what
 * is actually visible, so the app is drawn in that instead (spec §1.2).
 *
 * It also knows *where* that window is. Reaching the message box at the foot of a screen-tall page means iOS moves the
 * visible window down rather than scrolling a page that has nowhere to scroll, so the top of the screen stops being
 * the top of the layout. The app is moved down with it; otherwise it is drawn above what can be seen, which puts the
 * message box near the top of the screen with the keyboard a long way below it.
 */

/**
 * Inside the iOS app the keyboard plugin stops the web view reacting to the keyboard, so the visible window neither shrinks
 * nor moves for it. The plugin says how tall the keyboard will be as it starts to rise instead (native/keyboard), and the
 * app is drawn above that. Null in a browser, and in an app built before the plugin.
 */
let reported: number | null = null

/** A panel of the app's own in the keyboard's place (features/composer/AttachPanel): the app is drawn above it as above a keyboard. */
let panel = 0

/** The keyboard as last measured: 0 while none is up. */
let keyboardNow = 0

/** Below this, a change is a browser toolbar hiding or showing rather than a keyboard. */
const KEYBOARD_AT_LEAST = 120

/** The keyboard's height on this device, kept so a panel in its place matches it before the keyboard has been up this time. */
const KEYBOARD_HEIGHT_KEY = 'boss-chat.keyboard-height.v1'

/** Before a keyboard has been seen on this device, a panel takes a keyboard's usual share of the screen. */
const KEYBOARD_SHARE = 0.36

/**
 * The tallest the visible window has been at this width: the screen with no keyboard on it.
 *
 * A keyboard is how far short of that the window now falls. It cannot be read from innerHeight: iOS 27 shrinks the
 * layout along with the visible window (measured 796 → 449 against 796 → 447), so the two differ by 2 with the keyboard
 * up and comparing them never sees it.
 */
let tallest = 0
let measuredAtWidth = 0

function apply(): void {
  const viewport = window.visualViewport
  if (!viewport) return
  const height = Math.round(viewport.height)
  // Turning the phone starts again: the full height one way round is not the full height the other.
  const width = Math.round(viewport.width || window.innerWidth || 0)
  if (width !== measuredAtWidth) {
    tallest = 0
    measuredAtWidth = width
  }
  tallest = Math.max(tallest, height, Math.round(window.innerHeight))
  const covered = tallest - height
  const keyboard = reported ?? (covered >= KEYBOARD_AT_LEAST ? covered : 0)
  keyboardNow = keyboard
  if (keyboard > 0) remember(keyboard)
  // What lies under the app: the keyboard, or a panel in its place. A browser's visible window already leaves the keyboard out.
  const under = Math.max(keyboard, panel)
  const style = document.documentElement.style
  style.setProperty('--app-height', `${height - (reported === null ? under - keyboard : under)}px`)
  style.setProperty('--keyboard-inset', `${under}px`)
  style.setProperty('--viewport-offset', `${reported === null ? Math.round(viewport.offsetTop) : 0}px`)
  document.documentElement.classList.toggle('keyboard-open', under > 0)
  // The stylesheet moves the app on the keyboard's own curve: the height comes as the keyboard starts to move (layout.css).
  document.documentElement.classList.toggle('native-keyboard', reported !== null)
}

/** The keyboard's height as the app reports it (0 once it goes), or null to go back to the visible window. */
export function reportKeyboard(height: number | null): void {
  reported = height === null ? null : Math.max(0, Math.round(height))
  apply()
}

/** A panel of the app's own taking the keyboard's place, by its height (0 once it goes). */
export function reportPanel(height: number): void {
  panel = Math.max(0, Math.round(height))
  apply()
}

/** The keyboard's height now: 0 while none is up. */
export function keyboardHeightNow(): number {
  return keyboardNow
}

/** How tall a panel in the keyboard's place should be: the keyboard's height, now or as last seen on this device. */
export function keyboardRoom(): number {
  if (keyboardNow > 0) return keyboardNow
  const stored = Number(read(KEYBOARD_HEIGHT_KEY))
  if (stored > 0) return stored
  return Math.round((window.visualViewport?.height ?? window.innerHeight) * KEYBOARD_SHARE)
}

function remember(keyboard: number): void {
  if (read(KEYBOARD_HEIGHT_KEY) === String(keyboard)) return
  try {
    localStorage.setItem(KEYBOARD_HEIGHT_KEY, String(keyboard))
  } catch {
    // Storage can be unavailable: the panel then takes the usual share.
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Keeps the custom properties in step with the visible part of the screen, and where it is. Returns a cleanup. */
export function startViewportHeight(): () => void {
  const viewport = window.visualViewport
  if (!viewport) return () => {}
  tallest = 0
  measuredAtWidth = 0
  apply()
  viewport.addEventListener('resize', apply)
  viewport.addEventListener('scroll', apply)
  return () => {
    viewport.removeEventListener('resize', apply)
    viewport.removeEventListener('scroll', apply)
  }
}
